import React, { useEffect } from 'react'
import { MessageCircle, HelpCircle, FileText, Shield, MessageSquare } from 'lucide-react'

const ChatButton = ({ 
  type = 'general', // 'general', 'claims', 'policy', 'support'
  size = 'medium', // 'small', 'medium', 'large'
  variant = 'primary', // 'primary', 'secondary', 'outline'
  className = '',
  children,
  policyNumber = null, // Optional policy number for context
  claimNumber = null // Optional claim number for context
}) => {
  
  // Get the context message based on type
  const getContextMessage = () => {
    switch (type) {
      case 'claims':
        if (claimNumber && claimNumber !== 'N/A') {
          return `Hi, I need help with my insurance claim #${claimNumber}. Can you assist me?`
        }
        return 'Hi, I need help with my insurance claims. Can you assist me?'
      case 'policy':
        if (policyNumber && policyNumber !== 'N/A') {
          return `Hi, I have questions about my insurance policy #${policyNumber}. Can you help me?`
        }
        return 'Hi, I have questions about my insurance policy. Can you help me?'
      case 'support':
        return 'Hi, I need assistance with my insurance. How can you help me?'
      default:
        return null
    }
  }

  // Get the appropriate icon based on type
  const getIcon = () => {
    switch (type) {
      case 'claims':
        return <MessageSquare className="h-4 w-4" />
      case 'policy':
        return <MessageSquare className="h-4 w-4" /> // Updated to use MessageSquare icon
      case 'support':
        return <HelpCircle className="h-4 w-4" />
      default:
        return <MessageCircle className="h-4 w-4" />
    }
  }

  // Get button text based on type
  const getButtonText = () => {
    if (children) return children
    
    switch (type) {
      case 'claims':
        return 'Chat about Claims'
      case 'policy':
        return 'Chat about Policy'
      case 'support':
        return 'Get Support'
      default:
        return 'Chat with Us'
    }
  }

  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'px-3 py-1.5 text-sm'
      case 'large':
        return 'px-6 py-3 text-lg'
      default:
        return 'px-4 py-2 text-base'
    }
  }

  // Get variant classes
  const getVariantClasses = () => {
    switch (variant) {
      case 'secondary':
        return 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
      case 'outline':
        return 'bg-transparent text-blue-600 hover:bg-blue-50 border border-blue-300'
      default:
        return 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600'
    }
  }

  // Handle button click
  const handleClick = () => {
    if (!window.plugSDK?.toggleWidget) return

    const contextMessage = getContextMessage()
    
    const openChat = () => {
      try {
        if (contextMessage) {
          window.plugSDK.toggleWidget(true, 'create_conversation', {
            startConversationContent: contextMessage
          })
        } else {
          window.plugSDK.toggleWidget(true)
        }
      } catch (error) {
        // Fallback: just open
        try {
          window.plugSDK.toggleWidget(true)
        } catch (e) {
          // Silent fail
        }
      }
    }

    // Wait for widget to be ready
    if (window.plugSDK.isReady) {
      openChat()
    } else if (window.plugSDK.onReady) {
      window.plugSDK.onReady(() => {
        openChat()
      })
    } else {
      // No ready check available, just try
      openChat()
    }
  }

  // Get button styles
  const getButtonStyles = () => {
    const baseStyles = "inline-flex items-center space-x-2 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
    const sizeClasses = getSizeClasses()
    const variantClasses = getVariantClasses()
    
    return `${baseStyles} ${sizeClasses} ${variantClasses} ${className}`
  }

  return (
    <button
      onClick={handleClick}
      className={getButtonStyles()}
      title={children || getButtonText()}
    >
      {getIcon()}
      <span>{children || getButtonText()}</span>
    </button>
  )
}

export default ChatButton
